<script lang="ts">
    import s from './radio.module.css';

    import cn from 'classnames';
    import { fly } from 'svelte/transition';
    import { backOut } from 'svelte/easing';
    import { InputType } from '@uikit/form/types';
    import type { Props } from './types';

    import { type Action, setActionFunc } from '@uikit/actions';

    const transitionFlyOptions = {
        y: -5,
        duration: 200,
        easing: backOut,
        opacity: 1,
    };

    export let name: Props['name'];
    export let value: Props['value'] = undefined;
    export let label: Props['label'] = undefined;
    export let description: Props['description'] = undefined;
    export let error: Props['error'] = undefined;
    export let checked: Props['checked'] = false;
    export let disabled: Props['disabled'] = false;
    export let classes: Props['classes'] = '';
    export let attrs: Props['attrs'] = {};
    export let input: HTMLInputElement | undefined = undefined;
    export let group: Props['group'] = undefined;
    export let inputType: Props['inputType'] = InputType.Checkbox;
    export let action: Action | undefined = undefined;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    $: hasLabel = Boolean(label || $$slots.label);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    $: hasDescription = Boolean(description || $$slots.description);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    $: hasErrorMessage = Boolean((typeof error === 'string' && error.length > 0) || $$slots.error);

    $: isSelected = (inputType === InputType.Checkbox && checked) || (inputType === InputType.Radio && group === value);

    $: ({ actionFunc, actionParams } = setActionFunc(action));
</script>

<div
    class={cn(s.main, disabled && s.disabled, isSelected && s.checked, error && s.hasError, classes)}
    use:actionFunc={actionParams}
>
    <label class={s.label}>
        <div class={cn(s.control)}>
            {#if inputType === InputType.Checkbox}
                <input
                    type="checkbox"
                    class={s.controlInput}
                    {name}
                    {disabled}
                    {value}
                    {...attrs}
                    bind:checked
                    bind:this={input}
                    on:focus
                    on:blur
                    on:change
                />
            {:else if inputType === InputType.Radio}
                <input
                    type="radio"
                    class={s.controlInput}
                    {name}
                    {disabled}
                    {value}
                    {...attrs}
                    bind:group
                    bind:this={input}
                    on:focus
                    on:blur
                    on:change
                />
            {/if}
            {#if isSelected}
                <div class={s.controlIcon} transition:fly={transitionFlyOptions}></div>
            {/if}
        </div>
        {#if hasLabel || hasDescription}
            <div class={s.content}>
                {#if hasLabel}
                    <div class={s.labelText}>
                        <slot name="label">
                            {label}
                        </slot>
                    </div>
                {/if}
                {#if hasDescription}
                    <div class={s.description}>
                        <slot name="description">
                            {description}
                        </slot>
                    </div>
                {/if}
            </div>
        {/if}
    </label>
    {#if hasErrorMessage}
        <div class={s.error}>
            <slot name="error">
                {error}
            </slot>
        </div>
    {/if}
</div>
